import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/** Asigna una persona del directorio a una Iniciativa o Piloto con horas
 * dedicadas — coste = horas × tarifa (registrado en `personal_asignaciones`,
 * informativo/planificación, sin tocar los balances financieros existentes). */
export function AssignPersonDialog({ open, onOpenChange, person, onSaved }) {
  const { token } = useAuth();
  const [entityType, setEntityType] = useState("initiative");
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState("");
  const [horas, setHoras] = useState("");
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEntityId(""); setHoras(""); setNotas(""); setEntityType("initiative");
  }, [open, person]);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        const list = entityType === "initiative" ? await api.getInitiatives(token) : await api.getPilots(token);
        setEntities((list || []).map((e) => ({ id: e.id, name: e.name || e.title })).filter((e) => e.id && e.name));
      } catch (e) {
        setEntities([]);
      }
    };
    load();
  }, [open, entityType, token]);

  const handleSave = async () => {
    const entity = entities.find((e) => e.id === entityId);
    if (!entity) { toast.error("Selecciona una iniciativa o piloto"); return; }
    if (!horas || parseFloat(horas) <= 0) { toast.error("Indica las horas dedicadas"); return; }
    setSaving(true);
    try {
      await api.createPersonalAssignment(token, person.id, {
        entity_type: entityType, entity_id: entity.id, entity_name: entity.name,
        horas: parseFloat(horas), notas,
      });
      toast.success(`${person.nombre} ${person.apellidos} asignado/a a "${entity.name}"`);
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear la asignación");
    } finally {
      setSaving(false);
    }
  };

  if (!person) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[92vw] sm:max-w-md" data-testid="assign-person-dialog">
        <DialogHeader>
          <DialogTitle>Asignar a proyecto</DialogTitle>
          <DialogDescription>{person.nombre} {person.apellidos} · {person.zona_nombre} — {person.coste_hora ? `${person.coste_hora} €/h` : "sin coste/hora definido"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select value={entityType} onValueChange={setEntityType}>
              <SelectTrigger data-testid="assign-entity-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="initiative">Iniciativa / Portfolio</SelectItem>
                <SelectItem value="pilot">Piloto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Proyecto</Label>
            <Select value={entityId} onValueChange={setEntityId}>
              <SelectTrigger data-testid="assign-entity-select"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
              <SelectContent className="max-h-72">
                {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Horas dedicadas</Label>
            <Input type="number" min="0" step="0.5" value={horas} onChange={(e) => setHoras(e.target.value)} placeholder="Ej: 40" data-testid="assign-horas-input" />
            {horas && person.coste_hora ? (
              <p className="text-xs text-muted-foreground">Coste estimado: {(parseFloat(horas) * person.coste_hora).toFixed(2)} €</p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} data-testid="assign-notas-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="assign-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Asignar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
